import React, { useState, useEffect } from "react";
import { Box, Typography, CircularProgress, Stack, Chip } from "@mui/material";
import { History as HistoryIcon } from "@mui/icons-material";
import axios from "axios";
import dayjs from "dayjs";
import {
  API_BASE_URL,
  BG,
  BLUE,
  BLUE_DARK,
  BLUE_LIGHT,
  BLUE_MED,
  BLUE_PALE,
  BLUE_SUBTLE,
  BORDER,
  BORDER_MED,
  INK,
  INK_MUTED,
  INK_SOFT,
  SUCCESS,
  SUCCESS_BG,
} from "./shared";

// ─── Previous Loans Panel ─────────────────────────────────────────────────────
const PreviousLoansPanel = ({
  customerId,
  storeId,
  pawnMode,
  currentVoucher,
  onSelectLoan,
}) => {
  const [loans, setLoans] = useState([]);
  const [loading, setLoading] = useState(false);
  // Voucher currently being loaded via a click (shows a spinner on its card).
  const [loadingVoucher, setLoadingVoucher] = useState(null);

  // Click a previous loan to load it onto the screen for viewing/editing —
  // mirrors the receipt screen's rail. The already-loaded loan is inert.
  const handleClick = async (loan) => {
    const voucher = loan?.voucher_no;
    if (!voucher || !onSelectLoan) return;
    if (String(voucher) === String(currentVoucher)) return;
    if (loadingVoucher) return;
    setLoadingVoucher(voucher);
    try {
      await onSelectLoan(voucher);
    } finally {
      setLoadingVoucher(null);
    }
  };

  useEffect(() => {
    if (!customerId || storeId === undefined || storeId === null) {
      setLoans([]);
      return;
    }
    setLoading(true);
    const wantUnofficial = Number(pawnMode) === 2;
    axios
      .get(
        `${API_BASE_URL}/api/pawn/payments/customer/${storeId}/${customerId}`,
      )
      .then((res) => {
        if (res.data.success) {
          // Status 3 (official-clean) is shared between the books, so it lists
          // in both modes: official mode (1) lists status 1 (altered) + 3,
          // unofficial mode (2) lists status 2 + 3.
          const list = (res.data.data || []).filter((l) => {
            const st = Number(l.status);
            return wantUnofficial ? st === 2 || st === 3 : st !== 2;
          });
          setLoans(list);
        }
      })
      .catch(() => setLoans([]))
      .finally(() => setLoading(false));
  }, [customerId, storeId, pawnMode]);

  if (!customerId) {
    return (
      <Box sx={{ textAlign: "center", py: 4, px: 2 }}>
        <HistoryIcon sx={{ fontSize: 36, color: BORDER_MED, mb: 1 }} />
        <Typography
          variant="caption"
          sx={{ color: INK_MUTED, display: "block" }}
        >
          Select a customer to see their loan history
        </Typography>
      </Box>
    );
  }

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
        <CircularProgress size={24} sx={{ color: BLUE }} />
      </Box>
    );
  }

  if (!loans.length) {
    return (
      <Box sx={{ textAlign: "center", py: 3 }}>
        <Typography variant="caption" sx={{ color: INK_MUTED }}>
          No previous loans found
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1} sx={{ px: 0.5 }}>
      {loans.map((loan, i) => {
        const isCurrent =
          currentVoucher != null &&
          String(loan.voucher_no) === String(currentVoucher);
        const isLoadingThis =
          String(loadingVoucher) === String(loan.voucher_no);
        const clickable = !!onSelectLoan && !isCurrent;
        return (
          <Box
            key={i}
            onClick={clickable ? () => handleClick(loan) : undefined}
            role={clickable ? "button" : undefined}
            tabIndex={clickable ? 0 : undefined}
            onKeyDown={
              clickable
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleClick(loan);
                    }
                  }
                : undefined
            }
            sx={{
              p: 1.5,
              borderRadius: "8px",
              border: `1px solid ${isCurrent ? BLUE : BORDER}`,
              bgcolor: isCurrent ? BLUE_SUBTLE : BG,
              cursor: !clickable
                ? "default"
                : loadingVoucher
                ? "wait"
                : "pointer",
              outline: "none",
              transition:
                "border-color 0.15s ease, background-color 0.15s ease",
              ...(clickable && {
                "&:hover": { borderColor: BLUE_MED, bgcolor: BLUE_PALE },
                "&:focus-visible": {
                  borderColor: BLUE,
                  boxShadow: `0 0 0 3px ${BLUE_LIGHT}`,
                },
              }),
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 0.5,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{
                    color: isCurrent ? BLUE : INK_SOFT,
                    fontSize: "0.72rem",
                  }}
                >
                  #{loan.voucher_no || loan.id}
                </Typography>
                {isLoadingThis && (
                  <CircularProgress size={12} sx={{ color: BLUE }} />
                )}
              </Box>
              <Chip
                label={isCurrent ? "Loaded" : loan.status || "Active"}
                size="small"
                sx={{
                  height: 18,
                  fontSize: "0.65rem",
                  fontWeight: 700,
                  bgcolor: isCurrent
                    ? BLUE
                    : loan.status === "Closed"
                    ? SUCCESS_BG
                    : BLUE_LIGHT,
                  color: isCurrent
                    ? "#fff"
                    : loan.status === "Closed"
                    ? SUCCESS
                    : BLUE_DARK,
                }}
              />
            </Box>
            <Typography
              variant="body2"
              fontWeight={700}
              sx={{ color: INK, fontSize: "0.82rem" }}
            >
              ₹{(loan.loan_amount || 0).toLocaleString("en-IN")}
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: INK_MUTED, fontSize: "0.7rem" }}
            >
              {loan.transaction_date
                ? dayjs(loan.transaction_date).format("DD MMM YYYY")
                : "—"}
              {loan.scheme_name ? ` · ${loan.scheme_name}` : ""}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};

export default PreviousLoansPanel;
